// Saved meals: re-log a whole meal in one tap. The logged entry is a single
// combined food_entry (denormalized totals — deleting a saved meal never
// touches history), posted through the normal offline-queueable addFood path.
import { UtensilsCrossed, ChevronDown, Pencil } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import { useToast } from "../lib/toast";
import { useEntitlement } from "../lib/useEntitlement";
import { detailAllowance } from "../lib/detailGate";
import { useConfirm } from "../lib/confirm";
import { hapticSuccess, hapticWarning } from "../lib/haptics";
import { defaultMealSlot } from "../lib/date";
import { MealSlotPicker } from "./MealSlotPicker";
import type { MealSlot } from "@shared/types";
import { SheetShell } from "./SheetShell";
import { SaveMealNameSheet } from "./SaveMealNameSheet";
import { WeightStepper } from "./WeightStepper";
import { ListSkeleton } from "./ui/Skeleton";
import { EmptyState } from "./ui/EmptyState";
import type { SavedMeal } from "@shared/types";

interface Props {
  date: string; // YYYY-MM-DD
  onLogged: () => Promise<void> | void;
  onClose: () => void;
}

function totalsOf(meal: SavedMeal) {
  const sum = (f: (i: SavedMeal["items"][number]) => number | null) =>
    meal.items.reduce((acc, i) => acc + (f(i) ?? 0), 0);
  return {
    calories: Math.round(sum((i) => i.kcal)),
    protein_g: sum((i) => i.protein_g) || null,
    carbs_g: sum((i) => i.carbs_g) || null,
    fat_g: sum((i) => i.fat_g) || null,
    sugar_g: sum((i) => i.sugar_g) || null,
  };
}

export function SavedMealsSheet({ date, onLogged, onClose }: Props) {
  const t = useT();
  const toast = useToast();
  const { premium } = useEntitlement();
  const confirm = useConfirm();
  const [meals, setMeals] = useState<SavedMeal[] | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  // Tapping + opens a slot chooser instead of guessing from the clock
  // (owner request); the picker starts on the time-of-day default.
  const [logging, setLogging] = useState<{ meal: SavedMeal; slot: MealSlot } | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [renaming, setRenaming] = useState<SavedMeal | null>(null);
  // Per-meal edited items while a detail view is open; pristine copies keep
  // gram-scaling loss-free across repeated adjustments (same rule as the
  // estimate editor). null = no unsaved edits.
  const [draft, setDraft] = useState<{ mealId: number; items: SavedMeal["items"]; pristine: SavedMeal["items"] } | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.savedMeals()
      .then((m) => { if (!cancelled) setMeals(m); })
      .catch(() => { if (!cancelled) setMeals([]); });
    return () => { cancelled = true; };
  }, []);

  async function log(meal: SavedMeal, slot: MealSlot) {
    setBusyId(meal.id);
    try {
      const { calories, ...details } = totalsOf(meal);
      await api.addFood({
        eaten_on: date,
        source: "custom",
        plan_meal_slot: slot,
        name: meal.name,
        calories,
        // Detail amounts are Pro — a free one-tap log is the bare entry.
        ...detailAllowance(premium, details),
      });
      hapticSuccess();
      toast.success(t("toast.logged"));
      await onLogged();
      onClose();
    } catch {
      toast.error(t("toast.failed"));
      setBusyId(null);
    }
  }

  async function del(meal: SavedMeal) {
    if (!(await confirm({ message: t("saved.confirm_delete", { name: meal.name }), danger: true, confirmLabel: t("common.delete") }))) return;
    try {
      await api.deleteSavedMeal(meal.id);
      hapticWarning();
      setMeals((cur) => cur?.filter((m) => m.id !== meal.id) ?? cur);
    } catch {
      toast.error(t("common.failed"));
    }
  }

  function toggleOpen(meal: SavedMeal) {
    if (openId === meal.id) { setOpenId(null); setDraft(null); return; }
    setOpenId(meal.id);
    setDraft({ mealId: meal.id, items: meal.items.map((i) => ({ ...i })), pristine: meal.items.map((i) => ({ ...i })) });
  }

  /** Scale one item to new grams, linearly from its PRISTINE copy. */
  function setItemGrams(index: number, grams: number) {
    setDraft((d) => {
      if (!d) return d;
      const orig = d.pristine[index];
      if (!orig?.grams || orig.grams <= 0 || !(grams > 0)) return d;
      const f = grams / orig.grams;
      const scale = (v: number | null) => (v == null ? null : Math.round(v * f * 10) / 10);
      let micros_json = orig.micros_json;
      if (micros_json) {
        try {
          const m = JSON.parse(micros_json) as Record<string, number>;
          micros_json = JSON.stringify(Object.fromEntries(
            Object.entries(m).map(([k, v]) => [k, Math.round(v * f * 100) / 100])));
        } catch { /* keep as-is */ }
      }
      const items = d.items.map((it, j) => j === index ? {
        ...it,
        grams: Math.round(grams),
        kcal: Math.round(orig.kcal * f),
        protein_g: scale(orig.protein_g), carbs_g: scale(orig.carbs_g),
        fat_g: scale(orig.fat_g), sugar_g: scale(orig.sugar_g),
        micros_json,
      } : it);
      return { ...d, items };
    });
  }

  function removeItem(index: number) {
    setDraft((d) => d && d.items.length > 1
      ? { ...d, items: d.items.filter((_, j) => j !== index), pristine: d.pristine.filter((_, j) => j !== index) }
      : d);
  }

  const dirty = draft !== null && (() => {
    const meal = meals?.find((m) => m.id === draft.mealId);
    return !!meal && JSON.stringify(meal.items) !== JSON.stringify(draft.items);
  })();

  async function saveEdits() {
    if (!draft || savingEdit) return;
    setSavingEdit(true);
    try {
      await api.updateSavedMeal(draft.mealId, { items: draft.items.map(({ id: _i, ...rest }) => rest) });
      setMeals((cur) => cur?.map((m) => m.id === draft.mealId
        ? { ...m, items: draft.items }
        : m) ?? cur);
      setDraft((d) => d && { ...d, pristine: d.items.map((i) => ({ ...i })) });
      hapticSuccess();
      toast.success(t("toast.saved"));
    } catch {
      toast.error(t("toast.failed"));
    } finally {
      setSavingEdit(false);
    }
  }

  async function rename(meal: SavedMeal, name: string) {
    await api.updateSavedMeal(meal.id, { name });
    setMeals((cur) => cur?.map((m) => (m.id === meal.id ? { ...m, name } : m)) ?? cur);
    toast.success(t("toast.saved"));
  }

  return (
    <SheetShell onClose={onClose}>
      <h3 className="text-lg font-semibold mt-4 mb-4">{t("saved.title")}</h3>
      {meals === null ? (
        <ListSkeleton rows={4} />
      ) : meals.length === 0 ? (
        <div className="mb-4">
          <EmptyState message={t("saved.empty")} />
        </div>
      ) : (
        <div className="divide-y divide-line mb-2">
          {meals.map((m) => {
            const totals = totalsOf(m);
            return (
              <div key={m.id}>
              <div className="flex items-center gap-3 py-2.5">
                {m.emoji
                  ? <span aria-hidden="true" className="text-2xl w-9 text-center">{m.emoji}</span>
                  : <UtensilsCrossed aria-hidden="true" size={22} className="w-9 text-fg-muted" />}
                <button
                  onClick={() => setLogging({ meal: m, slot: defaultMealSlot(new Date().getHours()) })}
                  disabled={busyId !== null}
                  className="flex-1 min-w-0 text-left press disabled:opacity-50"
                >
                  <div className="text-sm font-medium truncate">{m.name}</div>
                  <div className="text-sm text-fg-muted">
                    {totals.calories} kcal · {m.items.length} {t("saved.items")}
                  </div>
                </button>
                <button
                  onClick={() => toggleOpen(m)}
                  aria-label={t("saved.details")}
                  aria-expanded={openId === m.id}
                  className="text-fg-muted p-3 -m-1 press"
                >
                  <ChevronDown size={18} className={`transition-transform ${openId === m.id ? "rotate-180" : ""}`} aria-hidden="true" />
                </button>
                <button
                  onClick={() => del(m)}
                  aria-label={t("common.delete")}
                  className="text-fg-faint p-3 -m-1 press"
                >✕</button>
                <button
                  onClick={() => setLogging({ meal: m, slot: defaultMealSlot(new Date().getHours()) })}
                  disabled={busyId !== null}
                  aria-label={t("add.add_item", { name: m.name })}
                  className="w-11 h-11 rounded-full bg-brand text-on-brand text-xl leading-none flex items-center justify-center flex-shrink-0 press disabled:opacity-50"
                >+</button>
              </div>
              {openId === m.id && draft?.mealId === m.id && (
                /* What's inside, adjustable: per-item grams rescale kcal and
                   macros linearly from pristine values; the last item can't be
                   removed (delete the meal instead). */
                <div className="pb-3 pl-12 pr-1 space-y-2">
                  {draft.items.map((it, idx) => (
                    <div key={idx} className="text-sm">
                      <div className="flex items-center gap-2">
                        <span className="flex-1 truncate text-fg-secondary">{it.name}</span>
                        <span className="tabular-nums text-fg-muted">{it.kcal} kcal</span>
                        {draft.items.length > 1 && (
                          <button
                            onClick={() => removeItem(idx)}
                            aria-label={t("common.delete")}
                            className="text-fg-faint px-2 py-1 press"
                          >✕</button>
                        )}
                      </div>
                      {it.grams != null && (
                        <div className="mt-1">
                          <WeightStepper
                            value={it.grams}
                            unit={(it.unit as "g" | "ml") ?? "g"}
                            onChange={(g) => setItemGrams(idx, g)}
                            aria-label={it.name}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                  <div className="flex items-center gap-2 pt-1">
                    <button
                      onClick={() => setRenaming(m)}
                      className="flex items-center gap-1.5 text-sm text-fg-secondary press py-2"
                    >
                      <Pencil size={14} aria-hidden="true" /> {t("saved.rename")}
                    </button>
                    {dirty && (
                      <button
                        onClick={saveEdits}
                        disabled={savingEdit}
                        className="ml-auto px-4 py-2 rounded-full bg-brand text-on-brand text-sm font-medium press disabled:opacity-50"
                      >
                        {t("saved.save_changes")}
                      </button>
                    )}
                  </div>
                </div>
              )}
              </div>
            );
          })}
        </div>
      )}
      {renaming && (
        <SaveMealNameSheet
          initial={renaming.name}
          onSave={(name) => rename(renaming, name)}
          onClose={() => setRenaming(null)}
        />
      )}
      {logging && (
        <SheetShell variant="center" onClose={() => setLogging(null)}>
          <div className="space-y-4 pt-1">
            <h2 className="text-lg font-semibold">{t("saved.log_where", { name: logging.meal.name })}</h2>
            <MealSlotPicker value={logging.slot} onChange={(slot) => setLogging((l) => l && { ...l, slot })} />
            <button
              onClick={() => { const l = logging; setLogging(null); void log(l.meal, l.slot); }}
              disabled={busyId !== null}
              className="w-full h-12 rounded-full bg-brand text-on-brand font-medium press disabled:opacity-50"
            >
              {t("today.add")}
            </button>
          </div>
        </SheetShell>
      )}
    </SheetShell>
  );
}
